
 // NexgbitsIS POS - isolated transaction workspaces.
 // Add this module beside posStore.js.
 // Does not change POS session lifecycle.

import { reactive, computed } from "vue";

// ============================================================
// CONFIGURATION
// ============================================================

const MAX_TABS = 3;

const WORKSPACE_KEY = "nexgbits-pos-sales-workspaces-v1";

// ============================================================
// TRANSACTION WORKSPACE FIELDS
// ============================================================

const FIELDS = [
  "cart",
  "customer",
  "membershipId",
  "loyaltyProgramId",
  "membership",
  "currency",
  "discountRate",
  "discountAmount",
  "taxCodeId",
  "taxCode",
  "tipRate",
  "tipAmount",
  "clientTransactionId",
  "sale",
  "creatingSale",
  "checkout",
  "checkoutDialog",
  "checkingOut",
  "paymentMethod",
  "paymentTransaction",
  "paymentStatus",
  "paymentError",
  "processingPayment",
  "stripeCheckout",
  "stripeCheckoutSessionId",
  "stripePaymentTransactionId",
  "stripePolling",
  "receipt",
  "error",
  "offlineInvoiceId"
];

// ============================================================
// DEFAULT TRANSACTION STATE
// ============================================================

const defaults = {
  cart: [],
  customer: null,
  membershipId: null,
  loyaltyProgramId: null,
  membership: null,
  currency: null,
  discountRate: null,
  discountAmount: null,
  taxCodeId: null,
  taxCode: null,
  tipRate: null,
  tipAmount: null,
  clientTransactionId: null,
  sale: null,
  creatingSale: false,
  checkout: null,
  checkoutDialog: false,
  checkingOut: false,
  paymentMethod: null,
  paymentTransaction: null,
  paymentStatus: null,
  paymentError: null,
  processingPayment: false,
  stripeCheckout: null,
  stripeCheckoutSessionId: null,
  stripePaymentTransactionId: null,
  stripePolling: false,
  receipt: null,
  error: null,
  offlineInvoiceId: null
};

// ============================================================
// SALES TAB REGISTRY
// ============================================================

const registry = reactive({
  sessionId: null,
  activeId: null,
  tabs: []
});

const activeTab = computed(() =>
  registry.tabs.find(
    tab => tab.id === registry.activeId
  ) ?? null
);

const canOpenTab = computed(() =>
  Boolean(registry.sessionId) &&
  registry.tabs.length < MAX_TABS
);

// ============================================================
// CLONE
// ============================================================

function clone(value) {
  if (value === undefined) {
    return undefined;
  }

  // JSON serialization unwraps nested Vue proxies in
  // cart/source objects.
  //
  // Do not persist non-serializable runtime handles in
  // a sales workspace.

  return JSON.parse(JSON.stringify(value));
}

// ============================================================
// SNAPSHOT TRANSACTION STATE
// ============================================================

function snapshot(state) {
  const result = {};

  for (const key of FIELDS) {
    result[key] = clone(state[key]);
  }

  return result;
}

// ============================================================
// RESTORE TRANSACTION STATE
// ============================================================

function restore(state, data) {
  for (const key of FIELDS) {
    const value = clone(
      data?.[key] ?? defaults[key]
    );

    if (key === "cart") {
      state.cart.splice(
        0,
        state.cart.length,
        ...(Array.isArray(value) ? value : [])
      );
    } else {
      state[key] = value;
    }
  }
}

// ============================================================
// GENERATE CLIENT TRANSACTION ID
// ============================================================

function id() {
  if (!globalThis.crypto?.randomUUID) {
    throw new Error(
      "Secure UUID generation is required."
    );
  }

  return globalThis.crypto.randomUUID();
}

// ============================================================
// GET CURRENT SESSION ID
// ============================================================

function getSessionId(state) {
  return (
    state.session?.id ??
    state.session?.sessionId ??
    null
  );
}

// ============================================================
// ASSERT ACTIVE SESSION
// ============================================================

function assertSession(state) {
  const sessionId = getSessionId(state);

  if (
    !sessionId ||
    String(sessionId) !== String(registry.sessionId)
  ) {
    throw new Error(
      "Sales tabs require the same active POS session."
    );
  }

  return sessionId;
}

// ============================================================
// ASSERT TAB SWITCH IS ALLOWED
// ============================================================

function assertSwitchable(state) {
  if (
    state.processingPayment ||
    state.creatingSale ||
    state.checkingOut ||
    state.stripePolling
  ) {
    throw new Error(
      "Finish the active sale or payment operation before switching tabs."
    );
  }

  const status = String(
    state.paymentStatus ?? ""
  ).toUpperCase();

  if (
    status === "PENDING" ||
    status === "PROCESSING"
  ) {
    throw new Error(
      "Resolve the pending payment before switching tabs."
    );
  }
}

// ============================================================
// PERSIST SALES WORKSPACES
// ============================================================

function persist() {
  if (!registry.sessionId) {
    return;
  }

  const data = {
    sessionId: registry.sessionId,
    activeId: registry.activeId,

    tabs: registry.tabs.map(tab => ({
      id: tab.id,
      createdAtUtc: tab.createdAtUtc,
      data: clone(tab.data)
    }))
  };

  sessionStorage.setItem(
    WORKSPACE_KEY,
    JSON.stringify(data)
  );
}

// ============================================================
// INITIALIZE SALES TABS
// ============================================================

function initialize(state) {
  const sessionId = getSessionId(state);

  if (!sessionId) {
    throw new Error(
      "Start a POS session first."
    );
  }

  if (
    registry.sessionId &&
    String(registry.sessionId) !== String(sessionId)
  ) {
    throw new Error(
      "Close or resolve the previous session's sales tabs first."
    );
  }

  if (registry.tabs.length) {
    return activeTab.value;
  }

  const raw = sessionStorage.getItem(
    WORKSPACE_KEY
  );

  if (raw) {
    try {
      const saved = JSON.parse(raw);

      if (
        String(saved.sessionId) === String(sessionId) &&
        Array.isArray(saved.tabs) &&
        saved.tabs.length > 0 &&
        saved.tabs.length <= MAX_TABS &&
        saved.tabs.every(
          tab =>
            tab.id &&
            tab.data &&
            tab.data.clientTransactionId === tab.id
        )
      ) {
        registry.sessionId = sessionId;

        registry.tabs.splice(
          0,
          registry.tabs.length,
          ...saved.tabs
        );

        registry.activeId = saved.tabs.some(
          tab => tab.id === saved.activeId
        )
          ? saved.activeId
          : saved.tabs[0].id;

        restore(
          state,
          activeTab.value.data
        );

        return activeTab.value;
      }
    } catch (error) {
      console.warn(
        "[POS] Sales tab restoration failed:",
        error
      );
    }
  }

  // A saved workspace from another session must never
  // be restored.
  //
  // Preserve it for investigation rather than silently
  // reusing its data.

  registry.sessionId = sessionId;

  const transactionId =
    state.clientTransactionId || id();

  state.clientTransactionId = transactionId;

  const tab = {
    id: transactionId,
    createdAtUtc: new Date().toISOString(),
    data: snapshot(state)
  };

  registry.tabs.push(tab);

  registry.activeId = tab.id;

  persist();

  return tab;
}

// ============================================================
// SAVE ACTIVE SALES TAB
// ============================================================

function saveActive(state) {
  assertSession(state);

  if (!activeTab.value) {
    throw new Error(
      "No active sales tab."
    );
  }

  activeTab.value.data = snapshot(state);

  persist();
}

// ============================================================
// OPEN NEW SALES TAB
// ============================================================

function open(state) {
  assertSession(state);

  assertSwitchable(state);

  if (!canOpenTab.value) {
    throw new Error(
      "Only three sales can be open at once."
    );
  }

  if (activeTab.value) {
    saveActive(state);
  }

  const transactionId = id();

  const data = clone(defaults);

  data.clientTransactionId = transactionId;

  data.currency =
    state.session?.currencyCode ??
    state.currency ??
    null;

  data.taxCodeId =
    state.taxCodeId ?? null;

  data.taxCode = clone(
    state.taxCode
  );

  const tab = {
    id: transactionId,
    createdAtUtc: new Date().toISOString(),
    data
  };

  registry.tabs.push(tab);

  registry.activeId = transactionId;

  restore(state, data);

  persist();

  return tab;
}

// ============================================================
// SELECT SALES TAB
// ============================================================

function select(state, transactionId) {
  assertSession(state);

  if (registry.activeId === transactionId) {
    return activeTab.value;
  }

  assertSwitchable(state);

  const next = registry.tabs.find(
    tab => tab.id === transactionId
  );

  if (!next) {
    throw new Error(
      "Sales tab not found."
    );
  }

  saveActive(state);

  registry.activeId = transactionId;

  restore(state, next.data);

  persist();

  return next;
}

// ============================================================
// CLOSE SALES TAB
// ============================================================

function close(
  state,
  transactionId,
  { completed = false } = {}
) {
  assertSession(state);

  if (transactionId !== registry.activeId) {
    throw new Error(
      "Select the sales tab before closing it."
    );
  }

  assertSwitchable(state);

  const tab = activeTab.value;

  if (!tab) {
    throw new Error(
      "Sales tab not found."
    );
  }

  if (
    !completed &&
    (
      state.cart.length ||
      state.sale?.id ||
      state.checkout?.id
    )
  ) {
    throw new Error(
      "Clear or resolve this transaction before closing its tab."
    );
  }

  const index = registry.tabs.findIndex(
    tab => tab.id === transactionId
  );

  registry.tabs.splice(
    index,
    1
  );

  const next =
    registry.tabs[
      Math.min(
        index,
        registry.tabs.length - 1
      )
    ] ?? null;

  if (next) {
    registry.activeId = next.id;

    restore(
      state,
      next.data
    );
  } else {
    registry.activeId = null;

    restore(
      state,
      defaults
    );
  }

  if (registry.tabs.length === 0) {
    sessionStorage.removeItem(
      WORKSPACE_KEY
    );
  } else {
    persist();
  }
}

// ============================================================
// CLEAR SALES TABS AFTER SESSION END
// ============================================================

function clearAfterSessionEnd() {
  registry.tabs.splice(0);

  registry.activeId = null;

  registry.sessionId = null;

  sessionStorage.removeItem(
    WORKSPACE_KEY
  );
}

// ============================================================
// EXPORT
// ============================================================

export default {
  registry,
  activeTab,
  canOpenTab,
  MAX_TABS,

  initialize,
  saveActive,
  open,
  select,
  close,
  clearAfterSessionEnd
};
